import React, { useMemo, useState } from 'react';
import { Box, Button, Grid } from '@mui/material';
import { MimshakData } from '../../interfaces/DTO/mimshakData';
import ScatterFundAmountChart from '../PersonFinancialData/Charts/ScatterFundAmountChart';
import YieldSummaryControl from './YieldSummaryControl';
import { fundService } from '../../services/fundService';
import { FundSumDropMap } from '../../interfaces/DTO/simulation';
import { ChangesDataUtils } from '../FeesLab/Reports/ChangesDataUtils';
import { computeWeightedYieldsFromPaths } from '../../services/yieldCalculator';

export type YieldMetric = 'cumulative_6m_yield' | 'cumulative_12m_yield' | 'avg_annual_yield_trailing_3yrs' | 'avg_annual_yield_trailing_5yrs';
export type { ExcludedPathEntry } from '../../services/yieldCalculator';

interface YieldLabProps {
  originalRows: MimshakData[];
  modifiedRows?: MimshakData[];
  fundSumDropMap?: FundSumDropMap;
  isModified?: boolean;
}

function buildPathsFromRows(rows: MimshakData[], fundSumDropMap?: FundSumDropMap) {
  const allPaths: { path: any; policyNumber?: string }[] = [];
  rows.forEach(row => {
    const additions = fundSumDropMap?.[row.policy_number];
    const extra = ChangesDataUtils.getTotalFundAddition(additions);
    const policies = row.data?.products?.flatMap(p => p.policies_or_accounts) || [];
    const rowPaths = policies.flatMap(p => p.investment_paths || []);
    const extraPerPath = extra && rowPaths.length > 0 ? extra / rowPaths.length : 0;
    rowPaths.forEach(path => allPaths.push({
      path: { ...path, accumulated_amount: (path.accumulated_amount ?? 0) + extraPerPath },
      policyNumber: row.policy_number,
    }));
  });
  return allPaths;
}

function toYieldValues(result: ReturnType<typeof computeWeightedYieldsFromPaths>) {
  return {
    yield6M:  result.cumulative_6m_yield.average,
    yield12M: result.cumulative_12m_yield.average,
    yield3Y:  result.avg_annual_yield_trailing_3yrs.average,
    yield5Y:  result.avg_annual_yield_trailing_5yrs.average,
    totalAmount: result.totalAmount,
    excluded6M:  result.cumulative_6m_yield.excluded,
    excluded12M: result.cumulative_12m_yield.excluded,
    excluded3Y:  result.avg_annual_yield_trailing_3yrs.excluded,
    excluded5Y:  result.avg_annual_yield_trailing_5yrs.excluded,
  };
}

const YieldLab: React.FC<YieldLabProps> = ({ originalRows, modifiedRows, fundSumDropMap, isModified }) => {
  const [view, setView] = useState<'original' | 'modified'>('original');
  const [selectedYieldField, setSelectedYieldField] = useState<YieldMetric>('cumulative_6m_yield');
  const [colorMode, setColorMode] = useState<'percentile_6m' | 'percentile_12m'>('percentile_6m');
  const [xAxisField, setXAxisField] = useState<'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year'>('stock_market_percent');
  const [yAxisMode, setYAxisMode] = useState<'yield' | 'stock_market_percent' | 'foreign_exposure_percent' | 'foreign_currency_exposure_percent' | 'liquidity_year' | 'standard_deviation'>('yield');

  const showModified = isModified && view === 'modified';
  const activeRows = showModified ? (modifiedRows ?? originalRows) : originalRows;
  const activeFundSumDropMap = showModified ? fundSumDropMap : undefined;

  const yieldRatingFundData = useMemo(() => {
    const fundIds = new Set<number>();
    activeRows.forEach(row => row.fund_ids?.forEach(id => fundIds.add(id)));
    return Array.from(fundIds).map(id => fundService.getFundFromCacheById(id)).filter(Boolean);
  }, [activeRows]);

  // Products for ScatterFundAmountChart (unchanged shape)
  const adjustedProducts = useMemo(() => {
    return activeRows.flatMap(row => {
      const additions = activeFundSumDropMap?.[row.policy_number];
      const extra = ChangesDataUtils.getTotalFundAddition(additions);
      if (!extra || !row.data?.products) return row.data?.products || [];
      return row.data.products.map(product => ({
        ...product,
        policies_or_accounts: (product.policies_or_accounts || []).map(policy => {
          const paths = policy.investment_paths || [];
          const extraPerPath = paths.length > 0 ? extra / paths.length : 0;
          return {
            ...policy,
            investment_paths: paths.map(path => ({
              ...path,
              accumulated_amount: (path.accumulated_amount ?? 0) + extraPerPath,
            })),
          };
        }),
      }));
    });
  }, [activeRows, activeFundSumDropMap]);

  const weightedYields = useMemo(
    () => toYieldValues(computeWeightedYieldsFromPaths(buildPathsFromRows(activeRows, activeFundSumDropMap))),
    [activeRows, activeFundSumDropMap]
  );

  const originalYields = useMemo(
    () => toYieldValues(computeWeightedYieldsFromPaths(buildPathsFromRows(originalRows))),
    [originalRows]
  );

  const neutralYields = useMemo(() => {
    if (!showModified || !modifiedRows) return toYieldValues(computeWeightedYieldsFromPaths(buildPathsFromRows(originalRows)));
    const originalPolicies = new Set(originalRows.map(r => r.policy_number));
    const filteredRows = modifiedRows.filter(r => originalPolicies.has(r.policy_number));
    return toYieldValues(computeWeightedYieldsFromPaths(buildPathsFromRows(filteredRows)));
  }, [showModified, modifiedRows, originalRows]);

  const hasNewFunds = useMemo(() => {
    if (!showModified) return false;
    const hasFundSumAdditions = !!fundSumDropMap && Object.values(fundSumDropMap).some(
      a => a && Object.values(a).some(v => v && v > 0)
    );
    if (hasFundSumAdditions) return true;
    const originalPolicies = new Set(originalRows.map(r => r.policy_number));
    return (modifiedRows ?? []).some(r => !originalPolicies.has(r.policy_number));
  }, [showModified, fundSumDropMap, originalRows, modifiedRows]);

  const handleMetricClick = (metric: YieldMetric) => {
    setSelectedYieldField(metric);
    if (metric === 'cumulative_6m_yield') setColorMode('percentile_6m');
    else if (metric === 'cumulative_12m_yield') setColorMode('percentile_12m');
    setXAxisField('stock_market_percent');
    setYAxisMode('yield');
  };

  return (
    <Box sx={{ backgroundColor: '#f5f5f5', borderRadius: '0 0 8px 8px', p: 2 }}>
      {isModified && (
        <Box display="flex" gap={1} mb={2} sx={{ backgroundColor: showModified ? '#fffbe6' : undefined, borderRadius: 1, p: 1 }}>
          <Button
            size="small"
            variant={view === 'original' ? 'contained' : 'outlined'}
            onClick={() => setView('original')}
          >
            לפני שינוי
          </Button>
          <Button
            size="small"
            variant={view === 'modified' ? 'contained' : 'outlined'}
            color="warning"
            onClick={() => setView('modified')}
          >
            אחרי שינוי
          </Button>
        </Box>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <ScatterFundAmountChart
            products={adjustedProducts}
            fundData={yieldRatingFundData}
            colorMode={colorMode}
            onColorModeChange={(mode) => setColorMode(mode as 'percentile_6m' | 'percentile_12m')}
            xAxisField={xAxisField}
            onXAxisFieldChange={(field) => setXAxisField(field)}
            yAxisMode={yAxisMode}
            onYAxisModeChange={(mode) => setYAxisMode(mode)}
            initialYieldField={selectedYieldField}
            yieldField={selectedYieldField}
            onYieldFieldChange={(field) => setSelectedYieldField(field as YieldMetric)}
            title={`מעבדת דירוג תשואות - ${
              selectedYieldField === 'cumulative_6m_yield' ? 'תשואה 6M' :
              selectedYieldField === 'cumulative_12m_yield' ? 'תשואה 12M' :
              selectedYieldField === 'avg_annual_yield_trailing_3yrs' ? 'תשואה שנתית 3Y' :
              'תשואה שנתית 5Y'
            } מול חשיפה למניות`}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <YieldSummaryControl
            yields={weightedYields}
            originalYields={showModified ? originalYields : undefined}
            neutralYields={showModified && hasNewFunds ? neutralYields : undefined}
            isModifiedView={showModified}
            onMetricClick={handleMetricClick}
            activeMetric={selectedYieldField}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default YieldLab;
